import React, { useState } from 'react';
import {
    Autocomplete, Box, IconButton, InputAdornment, TextField, Tooltip, Typography,
    Button, Collapse, Divider
} from "@mui/material";
import {
    Close as CloseIcon,
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from "@mui/icons-material";

const LanguageSelector = ({
    languages,
    selectedLanguage,
    customLanguage,
    customLanguageCode,
    customLanguageObject,
    onLanguageChange,
    onCustomLanguageChange,
    onCustomLanguageCodeChange
}) => {
    const [showCustomLanguage, setShowCustomLanguage] = useState(false);
    const disableAutocomplete = customLanguage || customLanguageCode;
    const disableTextFields = Boolean(selectedLanguage);
    const hasCustomLanguage = customLanguage || customLanguageCode;

    const handleToggleCustomLanguage = () => {
        setShowCustomLanguage(!showCustomLanguage);
        // Clear custom language fields when hiding
        if (showCustomLanguage) {
            onCustomLanguageChange({ target: { value: '' } });
            onCustomLanguageCodeChange({ target: { value: '' } });
        }
    };

    return (
        <div>
            {/* Main Language Selection */}
            <Tooltip
                title={
                    disableAutocomplete ? (
                        <Typography sx={{fontSize: '1rem'}}>
                            This field is disabled because a custom language is defined below.
                        </Typography>
                    ) : ""
                }
                arrow>
                <div>
                    <Autocomplete
                        options={languages}
                        getOptionLabel={(option) =>
                            typeof option === 'string'
                                ? option
                                : `${option.wikidataLabel}${option.nativeLabels ? ` (${option.nativeLabels})` : ''} [${option.ietfLanguageTag}]`
                        }
                        onChange={onLanguageChange}
                        value={selectedLanguage}
                        noOptionsText="Language not found - try adding a custom language below"
                        disabled={disableAutocomplete}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select your language"
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />
                </div>
            </Tooltip>

            {/* Custom Language Toggle Button */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 2,
                mb: showCustomLanguage ? 2 : 0
            }}>
                <Button
                    variant="text"
                    size="small"
                    startIcon={hasCustomLanguage ? <CloseIcon /> : <AddIcon />}
                    endIcon={showCustomLanguage ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={handleToggleCustomLanguage}
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: 'primary.main'
                        }
                    }}
                >
                    {hasCustomLanguage ? 'Remove Custom Language' : 'Add Custom Language'}
                </Button>
            </Box>

            {/* Collapsible Custom Language Fields */}
            <Collapse in={showCustomLanguage} timeout={300}>
                <Box sx={{ mt: 1 }}>
                    <Divider sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Custom Language
                        </Typography>
                    </Divider>
                    
                    <Tooltip
                        title={
                            disableTextFields ? (
                                <Typography sx={{fontSize: '1rem'}}>
                                    These fields are disabled because a language is already selected above.
                                </Typography>
                            ) : ""
                        }
                        arrow>
                        <div style={{display: 'flex', gap: '12px'}}>
                            <TextField
                                label="Language Name"
                                variant="outlined"
                                value={customLanguage}
                                onChange={onCustomLanguageChange}
                                disabled={disableTextFields}
                                size="small"
                                fullWidth
                                placeholder="e.g., Romansh"
                                slotProps={{
                                    input: {
                                        endAdornment: customLanguage && !disableTextFields ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => onCustomLanguageChange({ target: { value: '' } })}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <CloseIcon/>
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                            />
                            <TextField
                                label="Language Code"
                                variant="outlined"
                                value={customLanguageCode}
                                onChange={onCustomLanguageCodeChange}
                                disabled={disableTextFields}
                                size="small"
                                fullWidth
                                placeholder="e.g., rm"
                                slotProps={{
                                    input: {
                                        endAdornment: customLanguageCode && !disableTextFields ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => onCustomLanguageCodeChange({ target: { value: '' } })}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <CloseIcon/>
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                            />
                        </div>
                    </Tooltip>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Use ISO 639-1 language codes (e.g., "en" for English, "de" for German)
                    </Typography>
                </Box>
            </Collapse>
        </div>
    );
};

export default LanguageSelector;